/**
 * @fileoverview CRAR (Capital to Risk-Weighted Assets Ratio) vs regulatory-minimum trend chart.
 * @description Ported from the InsightBox Banking design system's own `charts/CrarTrend.tsx` — a
 * CRAR line over a shaded "buffer above minimum" area, with a dashed regulatory-minimum reference
 * line. `CHART_NAVY`/`CHART_LIGHT` there map to this app's `CHART_ACCENT`/`CHART_PRIMARY_LIGHT`
 * (see `TrendArea.tsx`'s file comment for the equivalent `CHART_BRAND` mapping); render-prop typing
 * uses recharts' own exported `LabelProps` instead of the reference's untyped `any`. Feature-scoped
 * rather than promoted to `shared/` since `CrarPoint` is banking-specific.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import {
  CHART_ACCENT,
  CHART_ERROR,
  CHART_GRID,
  CHART_PRIMARY_LIGHT,
} from '@/shared/constants/chart-theme.constants'

import type { LabelProps } from 'recharts'

export interface CrarPoint {
  fy: string
  crar: number
  min: number
  buffer: number
}

function makeCrarLabel(data: CrarPoint[]) {
  return function CrarLabel(props: LabelProps) {
    const { x, y, index } = props
    const point = index === undefined ? undefined : data[index]
    if (!point || typeof x !== 'number' || typeof y !== 'number') return null
    return (
      <text x={x} y={y - 14} textAnchor="middle" className="fill-accent text-[13px] font-semibold">
        {point.crar.toFixed(2)}%
      </text>
    )
  }
}

function makeMinLabel(data: CrarPoint[]) {
  return function MinLabel(props: LabelProps) {
    const { x, y, index } = props
    const point = index === undefined ? undefined : data[index]
    if (!point || typeof x !== 'number' || typeof y !== 'number') return null
    return (
      <text
        x={x}
        y={y + 18}
        textAnchor="middle"
        fill={CHART_ERROR}
        className="text-[11px] font-medium"
      >
        {point.min.toFixed(2)}%
      </text>
    )
  }
}

interface CrarTrendProps {
  data: CrarPoint[]
  height?: number | '100%'
}

/**
 * @component
 */
function CrarTrend({ data, height = 340 }: CrarTrendProps) {
  const max = Math.max(...data.map((d) => d.crar))
  const CrarLabel = makeCrarLabel(data)
  const MinLabel = makeMinLabel(data)

  return (
    // In fill mode (height="100%"), absolutely fill the nearest positioned ancestor instead of
    // relying on a plain CSS percentage height — see TrendArea.tsx for why. No-op for a fixed
    // numeric height.
    <ResponsiveContainer
      width="100%"
      height={height}
      debounce={200}
      {...(height === '100%' ? { style: { position: 'absolute' as const, inset: 0 } } : {})}
    >
      <ComposedChart data={data} margin={{ top: 36, right: 24, left: 8, bottom: 8 }}>
        <CartesianGrid stroke={CHART_GRID} vertical={false} />
        <XAxis
          dataKey="fy"
          axisLine={false}
          tickLine={false}
          tick={{ fill: '#111827', fontSize: 13, fontWeight: 600 }}
        />
        <YAxis
          domain={[0, Math.ceil((max * 1.3) / 2) * 2]}
          axisLine={false}
          tickLine={false}
          tick={{ fill: '#6B7280', fontSize: 11 }}
          width={36}
          unit="%"
        />
        {/* Without a formatter, recharts shows the raw stored number — "min" and "buffer" are
            derived via subtraction (e.g. 14.2 - 9 = 5.199999999999999 in floating point), so the
            tooltip needs its own rounding, same as the on-chart labels already do via
            .toFixed(2). */}
        <Tooltip
          formatter={(v) => `${Number(v).toFixed(2)}%`}
          contentStyle={{ borderRadius: 12, borderColor: '#E5E7EB' }}
        />
        <Legend
          verticalAlign="top"
          align="right"
          height={28}
          formatter={(value) => <span className="text-xs text-label">{value}</span>}
        />
        <Area
          dataKey="min"
          stackId="crar"
          stroke="none"
          fill="transparent"
          isAnimationActive={false}
          legendType="none"
        />
        <Area
          dataKey="buffer"
          stackId="crar"
          name="Buffer Above 9% Minimum"
          stroke="none"
          fill={CHART_PRIMARY_LIGHT}
          fillOpacity={0.5}
          isAnimationActive={false}
        />
        <Line
          dataKey="crar"
          name="CRAR (%)"
          stroke={CHART_ACCENT}
          strokeWidth={2.5}
          dot={{ r: 5, fill: CHART_ACCENT, stroke: '#fff', strokeWidth: 2 }}
          label={CrarLabel}
          isAnimationActive={false}
        />
        <Line
          dataKey="min"
          name="9% Minimum (Regulatory)"
          stroke={CHART_ERROR}
          strokeWidth={2}
          strokeDasharray="4 4"
          dot={{ r: 4, fill: CHART_ERROR, stroke: '#fff', strokeWidth: 1.5 }}
          label={MinLabel}
          isAnimationActive={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  )
}

export default CrarTrend
